import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import { alpha, useTheme } from "@mui/material/styles";
import Card from "@mui/material/Card";
import MDBox from "components/MDBox";
import type { IEnvioPedido } from "interfaces/loja/envio";
import { formatDate } from "helpers/formatDate";

type LinhaHistorico = {
  id: string;
  titulo: string;
  subtitulo?: string;
  quando: string;
};

function coletarLinhas(envios: IEnvioPedido[]): LinhaHistorico[] {
  const linhas: LinhaHistorico[] = [];
  for (const env of envios) {
    if (env.createAt) {
      const partes: string[] = [];
      if (env.status) partes.push(`Status: ${env.status}`);
      if (env.provedor) partes.push(env.provedor);
      if (env.servico) partes.push(env.servico);
      linhas.push({
        id: `${env.id}-milestone-criado`,
        titulo: "Envio registrado",
        subtitulo:
          partes.length > 0 ? partes.join(" · ") : "Registro criado no pedido",
        quando: env.createAt,
      });
    }
    if (env.enviadoEm) {
      linhas.push({
        id: `${env.id}-milestone-postado`,
        titulo: "Envio postado",
        subtitulo: "Data registrada no envio",
        quando: env.enviadoEm,
      });
    }
    if (env.entregueEm) {
      linhas.push({
        id: `${env.id}-milestone-entregue`,
        titulo: "Entrega registrada",
        subtitulo: "Data registrada no envio",
        quando: env.entregueEm,
      });
    }
    for (const ev of env.eventos ?? []) {
      linhas.push({
        id: ev.id,
        titulo: ev.status?.trim() || "Evento de envio",
        subtitulo: ev.descricao?.trim() || undefined,
        quando: ev.dataEvento,
      });
    }
  }
  linhas.sort(
    (a, b) => new Date(b.quando).getTime() - new Date(a.quando).getTime()
  );
  return linhas;
}

function fmt(iso: string) {
  try {
    return formatDate(iso, "dd/MM/yyyy HH:mm:ss");
  } catch {
    return iso;
  }
}

type EnvioHistoricoLogisticoProps = {
  envios: IEnvioPedido[];
};

/**
 * Lista resumida de eventos de envio (quando a API inclui `eventos` nos envios).
 */
export default function EnvioHistoricoLogistico({
  envios,
}: EnvioHistoricoLogisticoProps) {
  const theme = useTheme();
  const mode = theme.palette.mode;
  const borderSoft =
    mode === "dark" ? alpha("#c8e6c9", 0.45) : alpha("#7cb342", 0.22);

  const linhas = coletarLinhas(envios);

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 2,
        border: `1px solid ${borderSoft}`,
      }}
    >
      <MDBox p={2.5}>
        <Typography variant="button" fontWeight={700}>
          Histórico logístico
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
          Linha do tempo do envio: criação do registro, postagem, entrega e eventos
          adicionais (quando existirem).
        </Typography>
        {linhas.length === 0 ? (
          <Typography variant="body2" color="text.secondary" mt={2}>
            {envios.length === 0
              ? "Este pedido ainda não possui registro de envio. Crie o envio antes de acompanhar o histórico."
              : "Nenhuma data ou evento disponível para exibir. Após marcar como postado ou entregar, as datas aparecem aqui."}
          </Typography>
        ) : (
          <List dense disablePadding sx={{ mt: 1 }}>
            {linhas.map((l) => (
              <ListItem key={l.id} disableGutters sx={{ py: 0.75 }}>
                <ListItemText
                  primary={l.titulo}
                  secondary={
                    <>
                      {l.subtitulo ? (
                        <>
                          {l.subtitulo}
                          <br />
                        </>
                      ) : null}
                      {fmt(l.quando)}
                    </>
                  }
                  primaryTypographyProps={{ variant: "body2", fontWeight: 600 }}
                  secondaryTypographyProps={{ variant: "caption" }}
                />
              </ListItem>
            ))}
          </List>
        )}
      </MDBox>
    </Card>
  );
}
